import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';

import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';

import {
  EmailActionType,
  NewSmtpConfiguration,
  SmtpConfiguration,
  SmtpConfigurationResponse,
  SmtpTestEmailPayload
} from '../models/smtp-config.models';

@Injectable({
  providedIn: 'root'
})
export class SmtpConfigurationService {
  readonly #http = inject(ApiCallingV2Service);
  readonly #baseUrl = `${inject(API_CONFIG).baseUrlV2}/smtp-configuration`;

  /**
   * `includingInActive` widens the list rather than narrowing it: false returns the active
   * configurations alone, true returns every one of them.
   */
  getSmtpConfigurations(includingInActive: boolean): Observable<SmtpConfigurationResponse[]> {
    const params = new HttpParams().set('includingInActive', includingInActive);

    return this.#http
      .getData<SmtpConfigurationResponse[]>(this.#baseUrl, {
        showLoader: false,
        showMessage: false,
        options: { params }
      })
      .pipe(map((response: SmtpConfigurationResponse[] | null) => response ?? []));
  }

  getEmailActionTypes(): Observable<EmailActionType[]> {
    return this.#http
      .getData<EmailActionType[]>(`${this.#baseUrl}/email-action-types`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map((response: EmailActionType[] | null) => response ?? []));
  }

  addSmtpConfiguration(smtpConfiguration: NewSmtpConfiguration): Observable<SmtpConfigurationResponse> {
    return this.#http.postData<SmtpConfigurationResponse>(this.#baseUrl, { showLoader: false }, smtpConfiguration);
  }

  updateSmtpConfiguration(smtpConfiguration: SmtpConfiguration): Observable<SmtpConfigurationResponse> {
    return this.#http.putData<SmtpConfigurationResponse>(this.#baseUrl, smtpConfiguration, { showLoader: false });
  }

  /** The endpoint flips the stored flag itself, so there is nothing to send with it. */
  toggleSmtpConfiguration(id: string): Observable<SmtpConfigurationResponse> {
    return this.#http.putData<SmtpConfigurationResponse>(`${this.#baseUrl}/${id}/toggle`, undefined, {
      showLoader: false
    });
  }

  /**
   * The endpoint mails `sendTo`, or the configuration's own From address when that is blank, so
   * the confirmation names whichever one it was. Only the settings it validates are sent — the
   * stored id and the action type assignments play no part in reaching the server.
   *
   * Every SMTP fault reaches the client as a sanitized 500 that reads "An internal error has
   * occurred", the reason staying behind in the server log, so that failure is worded here
   * instead. A 400 is different: the server names what is wrong with the request (an Amazon SES
   * account without a valid configuration set, say), and that reason is shown as it is.
   *
   * `sendTo` goes out as null when blank — the API rejects an empty string as a malformed address.
   */
  sendTestEmail(smtpConfiguration: SmtpTestEmailPayload): Observable<unknown> {
    const { host, port, username, password, fromEmail, fromName, useSSL, provider, sesConfigurationSet } =
      smtpConfiguration;
    const sendTo = smtpConfiguration.sendTo?.trim() || null;

    return this.#http
      .postData(
        `${this.#baseUrl}/test-email`,
        {
          // The row already carries a spinner; the blocking one would only pile on.
          showLoader: false,
          // The failure is reported below, once the response says which kind it was.
          showError: false,
          message: `Test email sent to ${sendTo ?? fromEmail}.`
        },
        { host, port, username, password, fromEmail, fromName, useSSL, provider, sesConfigurationSet, sendTo }
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.#http.showMessage(
            this.#validationReason(error) ??
              `${host} did not accept the test email. Check the port, the SSL setting and the credentials.`,
            'error'
          );

          return throwError(() => error);
        })
      );
  }

  /** The API's own wording for a rejected request — its `message` is only a generic "Validation failed". */
  #validationReason(error: HttpErrorResponse): string | null {
    const errors: unknown = error.error?.errors;

    return error.status === 400 && Array.isArray(errors) && errors.length ? errors.join(' ') : null;
  }
}
